import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  deleteNotificationTemplate,
  listNotificationTemplates,
  sendPushBroadcast,
  upsertNotificationTemplate,
  type NotificationTemplateRecord,
  type PushBroadcastPayload,
} from '../../services/notifications';

const broadcastSchema = z.object({
  title: z.string().min(1, 'Başlık zorunludur'),
  body: z.string().min(1, 'İçerik zorunludur'),
  audience: z.enum(['all', 'segment', 'tokens']).default('all'),
  userIds: z.string().optional(),
  tokens: z.string().optional(),
  deepLink: z.string().optional(),
});

type BroadcastFormValues = z.infer<typeof broadcastSchema>;

const BROADCAST_DEFAULT: BroadcastFormValues = {
  title: '',
  body: '',
  audience: 'all',
  userIds: '',
  tokens: '',
  deepLink: '',
};

function parseList(raw: string | undefined) {
  if (!raw) return undefined;
  const list = raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
  return list.length > 0 ? list : undefined;
}

export default function NotificationCenter() {
  const queryClient = useQueryClient();
  const [selectedTemplateId, setSelectedTemplateId] = useState('');

  const {
    data: templates,
    error: templateError,
    isError,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ['notifications', 'templates'],
    queryFn: listNotificationTemplates,
    retry: false,
  });

  const broadcastForm = useForm<BroadcastFormValues>({
    resolver: zodResolver(broadcastSchema),
    defaultValues: BROADCAST_DEFAULT,
  });
  const audienceWatch = broadcastForm.watch('audience');
  const previewTitle = broadcastForm.watch('title') || 'Push başlığı';
  const previewBody = broadcastForm.watch('body') || 'Mesaj gövdesi bu alanda görünür.';

  const deleteMutation = useMutation({
    mutationFn: async (template: NotificationTemplateRecord) => deleteNotificationTemplate(template.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'templates'] });
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: upsertNotificationTemplate,
    onSuccess: (template) => {
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'templates'] });
      setSelectedTemplateId(template.id);
    },
  });

  const broadcastMutation = useMutation({
    mutationFn: (payload: PushBroadcastPayload) => sendPushBroadcast(payload),
  });
  const broadcastResult = broadcastMutation.data;
  const broadcastErrors = broadcastResult?.pushErrors ?? [];

  const onSubmitBroadcast = async (values: BroadcastFormValues) => {
    if (values.audience === 'segment') {
      const ids = parseList(values.userIds);
      if (!ids) {
        broadcastForm.setError('userIds', { type: 'manual', message: 'En az bir kullanıcı ID girin' });
        return;
      }
    }
    if (values.audience === 'tokens') {
      const tokenList = parseList(values.tokens);
      if (!tokenList) {
        broadcastForm.setError('tokens', { type: 'manual', message: 'En az bir Expo token girin' });
        return;
      }
    }

    const metadata: Record<string, unknown> = {};
    if (values.deepLink) {
      metadata.deepLink = values.deepLink.trim();
    }

    const payload: PushBroadcastPayload = {
      title: values.title,
      body: values.body,
      data: Object.keys(metadata).length > 0 ? metadata : undefined,
    };

    if (values.audience === 'segment') {
      payload.userIds = parseList(values.userIds);
      payload.tokens = undefined;
    } else if (values.audience === 'tokens') {
      payload.tokens = parseList(values.tokens);
      payload.userIds = undefined;
    } else {
      payload.userIds = undefined;
      payload.tokens = undefined;
    }

    await broadcastMutation.mutateAsync(payload);
    broadcastForm.reset(BROADCAST_DEFAULT);
  };

  const canManageTemplates = !isError;

  const templatesList = useMemo(() => templates ?? [], [templates]);
  const pushTemplates = useMemo(
    () => templatesList.filter((template) => template.channel === 'push'),
    [templatesList]
  );

  const applyTemplateToBroadcast = (templateId: string) => {
    setSelectedTemplateId(templateId);
    const template = templatesList.find((item) => item.id === templateId);
    if (!template) {
      return;
    }
    broadcastForm.setValue('title', template.title ?? '', { shouldDirty: true });
    broadcastForm.setValue('body', template.body ?? '', { shouldDirty: true });
  };

  const handleSaveBroadcastAsTemplate = async () => {
    if (!canManageTemplates) {
      return;
    }

    const values = broadcastForm.getValues();
    const title = values.title?.trim() ?? '';
    const body = values.body?.trim() ?? '';

    if (!title) {
      broadcastForm.setError('title', { type: 'manual', message: 'Başlık zorunludur' });
      return;
    }
    if (!body) {
      broadcastForm.setError('body', { type: 'manual', message: 'İçerik zorunludur' });
      return;
    }

    await saveTemplateMutation.mutateAsync({
      name: title,
      channel: 'push',
      title,
      body,
      isActive: true,
    });
  };

  return (
    <div className="card-grid">
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Şablon kütüphanesi</p>
            <p className="card-subtitle">
              {isLoading || isFetching ? 'Şablonlar yükleniyor...' : `${templatesList.length} kayıtlı şablon`}
            </p>
          </div>
        </div>

        {isError ? (
          <div className="empty-state">
            <strong>Şablon tablosu bulunamadı</strong>
            <p>
              {templateError instanceof Error
                ? templateError.message
                : "`notification_templates` tablosunu (id, name, channel, title, body, data_schema JSONB, is_active, created_at, updated_at) alanlarıyla oluşturun."}
            </p>
            <p className="helper-text">
              Tabloyu oluşturduktan sonra bu sayfayı yenileyerek şablonları görsel olarak yönetebilirsiniz.
            </p>
          </div>
        ) : templatesList.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz şablon yok</strong>
            <p>Kampanyaları hızlandırmak için tekrar kullanılabilir bildirim içerikleri kaydedin.</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Ad</th>
                <th>Kanal</th>
                <th>Durum</th>
                <th>Güncelleme</th>
                <th>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {templatesList.map((template) => (
                <tr key={template.id}>
                  <td>
                    <strong>{template.name}</strong>
                    <p className="helper-text">{template.title}</p>
                  </td>
                  <td>{template.channel}</td>
                  <td>
                    <span className={`chip ${template.isActive ? 'success' : 'warning'}`}>
                      {template.isActive ? 'Aktif' : 'Durduruldu'}
                    </span>
                  </td>
                  <td>
                    {new Date(template.updatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
                  </td>
                  <td>
                    <div className="form-row">
                      <button
                        type="button"
                        className="button secondary"
                        onClick={() => applyTemplateToBroadcast(template.id)}
                      >
                        Yükle
                      </button>
                      <button
                        type="button"
                        className="button danger"
                        onClick={() => {
                          if (window.confirm(`${template.name} şablonunu silmek istediğinize emin misiniz?`)) {
                            deleteMutation.mutate(template);
                          }
                        }}
                      >
                        Sil
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Toplu gönderim</p>
            <p className="card-subtitle">Supabase edge fonksiyonu ile Expo push bildirimlerini tetikleyin.</p>
          </div>
          <div className="form-row">
            {broadcastMutation.isSuccess ? (
              broadcastResult && (broadcastResult.failed ?? 0) > 0 ? (
                <span className="chip warning">
                  {`${broadcastResult.delivered}/${broadcastResult.attempted ?? broadcastResult.delivered} teslim`}
                </span>
              ) : (
                <span className="chip success">Gönderildi ✓</span>
              )
            ) : null}
            {saveTemplateMutation.isSuccess ? <span className="chip success">Şablon kaydedildi ✓</span> : null}
          </div>
        </div>

        <form className="form-grid" onSubmit={broadcastForm.handleSubmit(onSubmitBroadcast)}>
          {canManageTemplates ? (
            <div className="form-row">
              <label className="form-field">
                <span>Şablon seç (Push)</span>
                <select
                  value={selectedTemplateId}
                  onChange={(event) => applyTemplateToBroadcast(event.target.value)}
                >
                  <option value="">Şablon seçin</option>
                  {pushTemplates.map((template) => (
                    <option key={template.id} value={template.id}>
                      {template.name}
                    </option>
                  ))}
                </select>
                <span className="helper-text">Seçtiğiniz şablon başlık ve içerik alanlarını doldurur.</span>
              </label>
            </div>
          ) : (
            <div className="info-banner">
              <strong>Şablonlar devre dışı</strong>
              <p>Şablon kaydetmek için `notification_templates` tablosunu oluşturmanız gerekir.</p>
            </div>
          )}

          <div className="section-divider">1 · Mesaj içeriği</div>
          <div className="push-compose-grid">
            <div className="push-compose-fields">
              <label className="form-field">
                <span>Başlık</span>
                <input type="text" {...broadcastForm.register('title')} />
                {broadcastForm.formState.errors.title ? (
                  <span className="error-text">{broadcastForm.formState.errors.title.message}</span>
                ) : null}
              </label>

              <label className="form-field">
                <span>İçerik</span>
                <textarea rows={2} {...broadcastForm.register('body')} />
                {broadcastForm.formState.errors.body ? (
                  <span className="error-text">{broadcastForm.formState.errors.body.message}</span>
                ) : null}
              </label>
            </div>

            <div className="push-preview">
              <span className="helper-text">Alıcıların göreceği önizleme</span>
              <div className="push-preview__title">{previewTitle}</div>
              <div className="push-preview__body">{previewBody}</div>
              <label className="form-field">
                <span>Derin link / kampanya sayfası</span>
                <input type="text" placeholder="app://cart?coupon=FLASH10" {...broadcastForm.register('deepLink')} />
                <span className="helper-text">Kullanıcı bildirime dokunduğunda açılacak ekran (opsiyonel).</span>
              </label>
            </div>
          </div>

          <div className="section-divider">2 · Hedef kitle</div>
          <div className="segmented-control">
            <button
              type="button"
              className={audienceWatch === 'all' ? 'active' : ''}
              onClick={() => broadcastForm.setValue('audience', 'all', { shouldDirty: true })}
            >
              Tüm kullanıcılar
            </button>
            <button
              type="button"
              className={audienceWatch === 'segment' ? 'active' : ''}
              onClick={() => broadcastForm.setValue('audience', 'segment', { shouldDirty: true })}
            >
              Belirli kullanıcılar
            </button>
            <button
              type="button"
              className={audienceWatch === 'tokens' ? 'active' : ''}
              onClick={() => broadcastForm.setValue('audience', 'tokens', { shouldDirty: true })}
            >
              Expo token listesi
            </button>
          </div>

          <div className="form-row">
            {audienceWatch === 'segment' ? (
              <label className="form-field">
                <span>Kullanıcı ID'leri (virgülle ayırın)</span>
                <input type="text" placeholder="uuid-1, uuid-2" {...broadcastForm.register('userIds')} />
                {broadcastForm.formState.errors.userIds ? (
                  <span className="error-text">{broadcastForm.formState.errors.userIds.message}</span>
                ) : (
                  <span className="helper-text">Sadece bu kullanıcılar hedeflenir.</span>
                )}
              </label>
            ) : null}

            {audienceWatch === 'tokens' ? (
              <label className="form-field">
                <span>Expo tokenları</span>
                <input type="text" placeholder="ExponentPushToken[...]" {...broadcastForm.register('tokens')} />
                {broadcastForm.formState.errors.tokens ? (
                  <span className="error-text">{broadcastForm.formState.errors.tokens.message}</span>
                ) : (
                  <span className="helper-text">Önceden dışa aktardığınız token listesini girin.</span>
                )}
              </label>
            ) : null}
          </div>

          <div className="form-row end">
            <button
              type="button"
              className="button secondary"
              onClick={handleSaveBroadcastAsTemplate}
              disabled={saveTemplateMutation.isPending || !canManageTemplates}
            >
              {saveTemplateMutation.isPending ? 'Kaydediliyor...' : 'Şablon olarak kaydet'}
            </button>
            <button type="submit" className="button primary" disabled={broadcastMutation.isPending}>
              {broadcastMutation.isPending ? 'Gönderiliyor...' : 'Gönder'}
            </button>
          </div>

          {saveTemplateMutation.isError ? (
            <p className="error-text">
              {saveTemplateMutation.error instanceof Error
                ? saveTemplateMutation.error.message
                : 'Şablon kaydedilemedi'}
            </p>
          ) : null}

          {broadcastMutation.isError ? (
            <p className="error-text">
              {broadcastMutation.error instanceof Error
                ? broadcastMutation.error.message
                : 'Bildirim gönderilemedi'}
            </p>
          ) : null}

          {broadcastMutation.isSuccess && (broadcastResult?.failed ?? 0) > 0 ? (
            <div className="info-banner">
              <strong>
                {`${broadcastResult?.failed ?? 0} gönderim başarısız`}
              </strong>
              <p>
                {broadcastErrors[0]?.message ?? 'Push sağlayıcısı bazı cihazları kabul etmedi.'}
              </p>
              {(broadcastResult?.invalidTokensRemoved ?? 0) > 0 ? (
                <p className="helper-text">
                  {`${broadcastResult?.invalidTokensRemoved} geçersiz token veritabanından temizlendi.`}
                </p>
              ) : null}
            </div>
          ) : null}
        </form>
      </div>
    </div>
  );
}
